import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { NoSsr } from '@shared/components/NoSsr';
import { useFooterLinks } from './useFooterLinks';
import { FooterMenu } from './components/FooterMenu';
import { useTopLevelLinks } from '../useTopLevelLinks';
import { FooterSocial } from './components/FooterSocial';
import { useRouteMatch, useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { FooterPrivacyMenu } from './components/FooterPrivacyMenu';
import { ColumnsV2, Flex, ButtonV2, LinkV2 } from '@withjoy/joykit';
import { useShopStorefrontContext } from '@apps/registry/common/components/Catalog/components/ShopStorefrontProvider';
import {
  FooterMenuContainer,
  FooterMenuWrapper,
  FooterSubMenuWrapper,
  UniversalFooter,
  NewFlagText,
  UniversalFooterMenuWrapper,
  CopyrightText,
  FooterCopyrightContainer,
  UniversalFooterContainer,
  UniversalCtaContainer,
  StyledParticleJoyLogo,
  UniversalFooterSocialMenuWrapper,
  UniversalFooterWrapper,
  UniversalFooterLogoContainer,
  StyledFooter,
  ColumnContainer,
  styles
} from './AppFooter.styles';

interface Props {
  hidePrimaryFooter?: boolean;
}

export const AppFooter: React.FC<Props> = ({ hidePrimaryFooter = false }) => {
  const { itemsToRender } = useFooterLinks();
  const topLevelLinks = useTopLevelLinks();
  const { config } = useShopStorefrontContext();
  const [isMobile] = useResponsive<boolean>({ values: { mobile: true, tablet: true, desktop: false } });
  const isPdpPage = !!useRouteMatch({ path: ['/shop/item/:catalogItemId', '/:eventHandle/edit/registry/shop/item/:catalogItemId'] });

  const { t, t2 } = useTranslation('marcom');
  const footerNav = t2('footerNav');

  return (
    <StyledFooter id="marcom-footer">
      {!hidePrimaryFooter && (
        <NoSsr>
          <FooterMenuContainer paddingBottom={[8, null, null, pxToRem(122)]} __css={styles.footerMenuContainer}>
            <ColumnContainer __css={styles.columnContainer}>
              <ColumnsV2>
                <ColumnsV2.Column span={[12, 12, 7]}>
                  <FooterMenuWrapper flexDirection="column" paddingTop={[6, null, null, pxToRem(106)]} __css={styles.footerMenuWrapper}>
                    {itemsToRender('primary').map(menu => (
                      <ButtonV2
                        key={menu.id}
                        id={menu.id}
                        href={menu.href}
                        target={menu.target}
                        as="a"
                        intent="neutral"
                        variant="ghost"
                        textDecoration="none"
                        fontSize={[pxToRem(32), null, null, pxToRem(72)]}
                        lineHeight={[pxToRem(64), null, null, pxToRem(88)]}
                        height="100%"
                        typographyVariant={['HedMobile32', null, null, 'HedHero72']}
                        paddingX={[8, null, null, 6]}
                      >
                        {menu.label}
                      </ButtonV2>
                    ))}
                  </FooterMenuWrapper>
                </ColumnsV2.Column>
                <ColumnsV2.Column span={[12, 12, 5]}>
                  <Flex justifyContent={['flex-start', null, null, 'flex-end']} marginTop={[6, null, null, pxToRem(120)]}>
                    <FooterSubMenuWrapper justifyContent="flex-start" flexDirection="column" __css={styles.footerSubMenuWrapper}>
                      {itemsToRender('secondary').map(menu => (
                        <ButtonV2
                          key={menu.id}
                          id={menu.id}
                          href={menu.href}
                          target={menu.target}
                          typographyVariant="SectionEyebrow24"
                          textDecoration="none"
                          as="a"
                          intent="neutral"
                          variant="ghost"
                          fontSize={[pxToRem(20), null, null, pxToRem(24)]}
                          lineHeight={pxToRem(36)}
                          height="100%"
                          paddingX={[8, null, null, 6]}
                        >
                          {menu.label}
                        </ButtonV2>
                      ))}
                    </FooterSubMenuWrapper>
                  </Flex>
                </ColumnsV2.Column>
              </ColumnsV2>
            </ColumnContainer>
          </FooterMenuContainer>
        </NoSsr>
      )}
      <UniversalFooterWrapper backgroundColor="mono14" __css={styles.universalFooterWrapper(isPdpPage)}>
        <ColumnContainer __css={styles.columnContainer}>
          <ColumnsV2>
            <ColumnsV2.Column span={12}>
              <UniversalFooter flexDirection="column" paddingX={[8, null, null, 6]} paddingY={[8, null, null, 9]} __css={styles.universalFooter}>
                <UniversalFooterContainer justifyContent="space-between" __css={styles.universalFooterContainer}>
                  <UniversalFooterMenuWrapper __css={styles.universalFooterMenuWrapper}>
                    <LinkV2
                      id={topLevelLinks.joyWedding.id}
                      href={topLevelLinks.joyWedding.href}
                      target={topLevelLinks.joyWedding.target}
                      typographyVariant="FeatureCopy17"
                      textDecoration="none"
                      color="white"
                      width={['100%', null, null, 'initial']}
                      _visited={{ color: 'white' }}
                      _hover={{ color: 'mono5' }}
                      _active={{ color: 'white' }}
                      onClick={topLevelLinks.joyWedding.onClick}
                    >
                      {topLevelLinks.joyWedding.label}
                    </LinkV2>
                    <LinkV2
                      id={topLevelLinks.joyBaby.id}
                      href={topLevelLinks.joyBaby.href}
                      target={topLevelLinks.joyBaby.target}
                      typographyVariant="FeatureCopy17"
                      textDecoration="none"
                      color="white"
                      display="flex"
                      alignItems="center"
                      _visited={{ color: 'white' }}
                      _hover={{ color: 'mono5' }}
                      _active={{ color: 'white' }}
                      onClick={topLevelLinks.joyBaby.onClick}
                    >
                      {topLevelLinks.joyBaby.label}
                      <NewFlagText typographyVariant="Dek20" color="mono14" tagName="span" marginLeft={3} __css={styles.newFlagText}>
                        {footerNav.new}
                      </NewFlagText>
                    </LinkV2>
                  </UniversalFooterMenuWrapper>
                  {!isMobile && <FooterMenu />}
                </UniversalFooterContainer>
                <UniversalFooterContainer
                  flexDirection={['column', null, null, 'row']}
                  justifyContent="space-between"
                  alignItems={['flex-start', null, null, 'center']}
                  __css={styles.universalFooterContainer}
                >
                  <UniversalCtaContainer flexDirection={['column', null, null, 'row']} __css={styles.universalCtaContainer} width={['100%', null, null, 'initial']}>
                    <NoSsr>
                      <ButtonV2
                        id="universalFooterGetStarted"
                        as="a"
                        href={topLevelLinks.getStarted.href}
                        target={topLevelLinks.getStarted.target}
                        onClick={topLevelLinks.getStarted.onClick}
                        color="mono14"
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'white', color: 'primary6' }}
                        _active={{ backgroundColor: 'white', color: 'primary8' }}
                        width={['100%', null, null, pxToRem(174)]}
                      >
                        {topLevelLinks.getStarted.label}
                      </ButtonV2>
                    </NoSsr>
                    <ButtonV2
                      id="universalFooterLogin"
                      backgroundColor="transparent"
                      _hover={{ backgroundColor: 'mono12' }}
                      _active={{ backgroundColor: 'mono10' }}
                      border={['2px solid', null, null, 'none']}
                      borderColor="white"
                      as="a"
                      href={topLevelLinks.login.href}
                      target={topLevelLinks.login.target}
                      onClick={topLevelLinks.login.onClick}
                      width={['100%', null, null, pxToRem(174)]}
                    >
                      {topLevelLinks.login.label}
                    </ButtonV2>
                    <ButtonV2
                      id="universalFooterFindAnEvent"
                      backgroundColor="transparent"
                      _hover={{ backgroundColor: 'mono12' }}
                      _active={{ backgroundColor: 'mono10' }}
                      as="a"
                      href={topLevelLinks.findACouple.href}
                      target={topLevelLinks.findACouple.target}
                      onClick={topLevelLinks.findACouple.onClick}
                      width={['100%', null, null, pxToRem(174)]}
                    >
                      {topLevelLinks.findACouple.label}
                    </ButtonV2>
                  </UniversalCtaContainer>
                  {isMobile && (
                    <Flex flexDirection="column" rowGap={9}>
                      <FooterMenu />
                      <FooterPrivacyMenu />
                    </Flex>
                  )}
                  <UniversalFooterLogoContainer flexDirection={['column', null, null, 'row']} __css={styles.universalFooterLogoContainer}>
                    <UniversalFooterSocialMenuWrapper justifyContent="center" __css={styles.universalFooterSocialMenuWrapper}>
                      <FooterSocial />
                    </UniversalFooterSocialMenuWrapper>
                    <StyledParticleJoyLogo id="universalFooterLogo" href={config.homePageUrl} logoColor="white" />
                  </UniversalFooterLogoContainer>
                </UniversalFooterContainer>
                <FooterCopyrightContainer justifyContent="space-between" paddingTop={3} __css={styles.footerCopyrightContainer}>
                  <CopyrightText typographyVariant="body1" tagName="span" __css={styles.copyrightText}>
                    {t('footerNav', 'copyright')({ year: new Date().getFullYear() })}
                  </CopyrightText>
                  {!isMobile && <FooterPrivacyMenu />}
                </FooterCopyrightContainer>
              </UniversalFooter>
            </ColumnsV2.Column>
          </ColumnsV2>
        </ColumnContainer>
      </UniversalFooterWrapper>
    </StyledFooter>
  );
};
